// frontend/src/api/client.ts
// ============================================================================
// RAVERS NETWORK - Типизированный HTTP API клиент
// Включает: XHR побайтовую загрузку с прогрессом (0% -> 100%), REST эндпоинты
// ============================================================================

import type {
  AttachmentProto,
  AuthResponse,
  ChatContextResponse,
  FriendsListResponse,
  UserProfileView,
  VaultChallengeResponse,
} from '@/types';

// ============================================================================
// КЛАССЫ СЕТЕВЫХ ОШИБОК
// ============================================================================

export class ApiError extends Error {
  public status: number;
  public details?: any;

  constructor(status: number, message: string, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'Сессия истекла или не авторизована') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

export class MaintenanceError extends ApiError {
  public hubId?: string;

  constructor(message: string, hubId?: string) {
    super(503, message);
    this.name = 'MaintenanceError';
    this.hubId = hubId;
  }
}

// ============================================================================
// БАЗОВЫЙ МЕТОД ЗАПРОСОВ (FETCH WRAPPER)
// ============================================================================

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const isFormData = options.body instanceof FormData;

  const headers = new Headers(options.headers || {});
  headers.set('Accept', 'application/json');

  if (!isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(endpoint, {
    ...options,
    headers,
    credentials: 'include', // Обязательно для передачи HttpOnly кук сессий
  });

  if (response.status === 503) {
    const errorJson = await response.json().catch(() => ({}));
    throw new MaintenanceError(
      errorJson.message || 'Плановое обновление платформы',
      errorJson.hub_id
    );
  }

  if (response.status === 401) {
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    const errorJson = await response.json().catch(() => ({}));
    const message = errorJson.error || errorJson.message || `Ошибка сервера (${response.status})`;
    throw new ApiError(response.status, message, errorJson);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

// ============================================================================
// ЭКСПОРТ МОДУЛЕЙ API
// ============================================================================

export const api = {
  // 1. Аутентификация, профиль и ключи
  auth: {
    register(payload: {
      login: string;
      password: string;
      country: string;
      recovery_phrase?: string;
      public_key?: string;
      vault_payload?: string;
    }): Promise<AuthResponse> {
      return request<AuthResponse>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    login(payload: { login: string; password: string; country: string }): Promise<AuthResponse> {
      return request<AuthResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    getMe(): Promise<UserProfileView> {
      return request<UserProfileView>('/api/auth/me', {
        method: 'GET',
      });
    },

    async uploadAvatar(file: File): Promise<{ status: string; avatar_url: string }> {
      const formData = new FormData();
      formData.append('avatar', file);
      return request<{ status: string; avatar_url: string }>('/api/auth/avatar', {
        method: 'POST',
        body: formData,
      });
    },

    getMyVault(): Promise<VaultChallengeResponse> {
      return request<VaultChallengeResponse>('/api/auth/vault', {
        method: 'GET',
      });
    },

    getVaultChallenge(payload: { login: string; country: string }): Promise<VaultChallengeResponse> {
      return request<VaultChallengeResponse>('/api/auth/vault/challenge', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },

    updateMyPubkey(publicKey: string): Promise<{ status: string; message: string }> {
      return request<{ status: string; message: string }>('/api/auth/pubkey', {
        method: 'POST',
        body: JSON.stringify({ public_key: publicKey }),
      });
    },

    getUserPubkey(targetId: string): Promise<{ user_id: string; login: string; public_key: string | null }> {
      return request<{ user_id: string; login: string; public_key: string | null }>(
        `/api/auth/pubkey/${targetId}`,
        { method: 'GET' }
      );
    },

    logout(): Promise<{ status: string }> {
      return request<{ status: string }>('/api/auth/logout', {
        method: 'POST',
      });
    },
  },

  // 2. Друзья и граф связей
  friends: {
    getFriends(): Promise<FriendsListResponse> {
      return request<FriendsListResponse>('/api/friends', {
        method: 'GET',
      });
    },

    sendRequest(targetLogin: string): Promise<{ status: string; target_id: string }> {
      return request<{ status: string; target_id: string }>('/api/friends/request', {
        method: 'POST',
        body: JSON.stringify({ target_login: targetLogin }),
      });
    },

    acceptRequest(targetId: string): Promise<{ status: string }> {
      return request<{ status: string }>('/api/friends/accept', {
        method: 'POST',
        body: JSON.stringify({ target_id: targetId }),
      });
    },

    cancelRequest(targetId: string): Promise<{ status: string }> {
      return request<{ status: string }>('/api/friends/cancel', {
        method: 'POST',
        body: JSON.stringify({ target_id: targetId }),
      });
    },

    removeFriend(targetId: string): Promise<{ status: string }> {
      return request<{ status: string }>('/api/friends/remove', {
        method: 'POST',
        body: JSON.stringify({ target_id: targetId }),
      });
    },
  },

  // 3. Диалоги и медиа-вложения (с побайтовым XHR прогрессом 0-100%)
  chat: {
    getContext(targetId: string): Promise<ChatContextResponse> {
      return request<ChatContextResponse>(`/api/chat/context/${targetId}`, {
        method: 'GET',
      });
    },

    /**
     * Побайтовая загрузка файла через XMLHttpRequest с отслеживанием прогресса 0% -> 100%
     */
    uploadMedia(
      file: File | Blob,
      fileName = 'file.bin',
      durationSecs?: number,
      onProgress?: (percent: number, loaded: number, total: number) => void
    ): Promise<AttachmentProto> {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file, fileName);
        if (file.type) {
          formData.append('mime_type', file.type);
        }
        if (durationSecs !== undefined) {
          formData.append('duration', durationSecs.toString());
        }

        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/chat/upload', true);
        xhr.withCredentials = true; // Передаем HttpOnly куку сессии

        if (xhr.upload && onProgress) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const percent = Math.min(99, Math.round((e.loaded / e.total) * 100));
              onProgress(percent, e.loaded, e.total);
            }
          };
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const json = JSON.parse(xhr.responseText) as AttachmentProto;
              if (onProgress) onProgress(100, 100, 100);
              resolve(json);
            } catch {
              reject(new ApiError(xhr.status, 'Некорректный JSON-ответ от сервера'));
            }
          } else if (xhr.status === 401) {
            reject(new UnauthorizedError());
          } else {
            reject(new ApiError(xhr.status, `Ошибка сервера (${xhr.status})`));
          }
        };

        xhr.onerror = () => reject(new ApiError(0, 'Сетевая ошибка при передаче файла'));
        xhr.ontimeout = () => reject(new ApiError(0, 'Таймаут соединения при загрузке'));

        xhr.send(formData);
      });
    },
  },
};
